/**
 * Cascading Text Animation Module
 * Creates a cascading "enter" effect for text elements
 * 
 * @module cascadingText
 * @example
 * // HTML usage:
 * // <h1 class="page-title cascading-text cascading-text--enter" data-animated>Title…</h1>
 * // Optional attributes: data-interval="50" data-duration="600"
 */

// Constants
const ANIM_ATTR = 'data-animated';
const LETTER_CLASS = 'cascading-text__letter';
const DEFAULT_INTERVAL = 90; // ms between letters (stagger)
const DEFAULT_DURATION = 700; // ms animation per letter

/**
 * Splits the text content of an element into individual letter spans
 * @private
 * @param {HTMLElement} el - The element containing text to split
 * @returns {NodeList} List of created letter elements
 */
function splitToLetters(el) {
    if (!el) return [];
    if (el.__cascadingSplit) return el.querySelectorAll(`.${LETTER_CLASS}`) || [];
    
    const text = el.textContent;
    el.textContent = '';
    const frag = document.createDocumentFragment();
    
    for (const ch of text) {
        const span = document.createElement('span');
        span.className = LETTER_CLASS;
        // Preserve spaces visually using NBSP so inline-block spans keep width
        span.textContent = ch === ' ' ? '\u00A0' : ch;
        frag.appendChild(span);
    }
    
    el.appendChild(frag);
    el.__cascadingSplit = true;
    return el.querySelectorAll(`.${LETTER_CLASS}`);
}

/**
 * Applies the cascading enter animation to an element
 * @private
 * @param {HTMLElement} el - The element to animate
 * @param {Object} options - Animation options
 * @param {number} [options.interval=DEFAULT_INTERVAL] - Time between letter animations in ms
 * @param {number} [options.duration=DEFAULT_DURATION] - Duration of each letter's animation in ms
 */
function applyEnter(el, { interval = DEFAULT_INTERVAL, duration = DEFAULT_DURATION } = {}) {
    const letters = splitToLetters(el);
    
    // Apply animation properties to each letter
    letters.forEach((node, i) => {
        node.style.animationDuration = `${duration}ms`;
        node.style.animationDelay = `${interval * (i + 1)}ms`;
    });
    
    // Calculate total animation time
    const total = interval * letters.length + duration + 50;
    
    // Set CSS custom property for total duration if needed
    try {
        document.documentElement.style.setProperty('--title-enter-total', `${total}ms`);
    } catch (e) {
        // Silently fail if setting the property fails
        console.debug('Could not set CSS variable --title-enter-total', e);
    }
    
    // Clean up after animation completes
    setTimeout(() => {
        el.removeAttribute(ANIM_ATTR);
        letters.forEach(letter => {
            letter.style.animationDelay = '';
            letter.style.animationDuration = '';
        });
    }, total);
}

/**
 * Initializes the cascading text animation on all matching elements
 * @private
 */
function init() {
    const elements = document.querySelectorAll(`.cascading-text--enter[${ANIM_ATTR}]`);
    elements.forEach(el => {
        applyEnter(el, {
            interval: Number(el.dataset.interval) || DEFAULT_INTERVAL,
            duration: Number(el.dataset.duration) || DEFAULT_DURATION,
        });
    });
}

/**
 * Applies cascading enter animation to the specified target(s)
 * @param {string|HTMLElement} target - CSS selector or DOM element to animate
 * @param {Object} [opts] - Animation options
 * @param {number} [opts.interval] - Time between letter animations in ms
 * @param {number} [opts.duration] - Duration of each letter's animation in ms
 * @example
 * import { applyCascadingEnter } from './js/cascading-text.js';
 * applyCascadingEnter('.my-text', { interval: 100, duration: 800 });
 */
function applyCascadingEnter(target, opts = {}) {
    if (typeof target === 'string') {
        document.querySelectorAll(target).forEach(el => applyEnter(el, opts));
    } else if (target instanceof Element) {
        applyEnter(target, opts);
    }
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    // DOM already loaded, initialize immediately
    init();
}

// Export the public API
export { applyCascadingEnter };
export default applyCascadingEnter;
